import React, { useState } from 'react';
import { Send, Loader2, AlertCircle } from 'lucide-react';
import { Button } from "../ui/button";
import { cn } from "../../lib/utils";

interface UploadButtonProps {
  uploading: boolean;
  disabled: boolean;
  onClick: () => void;
  onDisabledClick?: () => void;
  buttonText?: string;
  isTryUpload?: boolean;
  isDryRun?: boolean;
  disabledReasons?: string[];
}

export default function UploadButton({
  uploading,
  disabled,
  onClick,
  onDisabledClick,
  buttonText,
  isTryUpload = false,
  isDryRun = false,
  disabledReasons = [],
}: UploadButtonProps): JSX.Element {
  const [showReasons, setShowReasons] = useState(false);

  const getButtonText = () => {
    if (buttonText) return buttonText;
    if (uploading) return 'Publishing...';
    if (isTryUpload) return 'Try Posting';
    if (isDryRun) return 'Simulated Posting';
    return 'Publish Post';
  };

  const handleClick = (e: React.MouseEvent) => {
    if (disabled && !uploading) {
      e.preventDefault();
      setShowReasons(true);
      if (onDisabledClick) onDisabledClick();
      // Hide reasons after 3 seconds
      setTimeout(() => setShowReasons(false), 3000);
      return;
    }
    if (!disabled) {
      onClick();
    }
  };

  return (
    <div className="space-y-3 w-full">
      <Button
        onClick={handleClick}
        className={cn(
          "w-full h-14 rounded-2xl font-black text-lg tracking-tight shadow-xl transition-all active:scale-[0.98]",
          isDryRun 
            ? "bg-amber-500 hover:bg-amber-600 text-[#0f172a] shadow-amber-500/10" 
            : "bg-primary hover:bg-primary/90 text-white shadow-primary/20",
          disabled && !uploading && "opacity-50 cursor-not-allowed hover:bg-primary active:scale-100"
        )}
      >
        {uploading ? (
          <Loader2 className="mr-3 h-6 w-6 animate-spin" />
        ) : (
          <Send className={cn("mr-3 h-5 w-5", isDryRun ? "fill-[#0f172a]" : "fill-white")} />
        )}
        {getButtonText()}
      </Button>

      {showReasons && disabledReasons.length > 0 && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-3 flex flex-col gap-1.5 animate-in slide-in-from-top-2 duration-300">
          {disabledReasons.map((reason, i) => (
            <div key={i} className="flex items-center gap-2 text-red-400 text-xs font-bold">
              <AlertCircle className="h-3 w-3 shrink-0" />
              {reason}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
